import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  fetchCasaMapaAdminAgency,
  patchCasaMapaAdminAgencyCrm,
  patchCasaMapaAdminAgencyProfile,
  startCasaMapaAdminAgencyPortalSession,
} from "../../../api/clientCasamapaAdminApi";
import { resolveClientPortalAssetUrl } from "../../../api/clientUploadApi";
import ClientPortalImageUpload from "../../../components/clientPortal/ClientPortalImageUpload";
import AgencyCrmWorkspace from "../../../components/casamapa/AgencyCrmWorkspace";
import { CASAMAPA_PARTNER_PORTAL_BASE } from "../../../constants/casamapaPartnerPortalConfig";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import { useLocale, useCurrency } from "../../../intl";
import { formatClientDateTime, formatStatusLabel } from "../../../utils/clientPortalFormat";
import "../../../styles/partnerAgenciesPage.css";
import "../../../styles/casamapaAgencyAdminDetail.css";

const COPY = "realEstate.casaMapa.admin.agencies";
const PROFILE_COPY = `${COPY}.profile`;

const PAGE_TABS = [
  { id: "overview", labelKey: `${PROFILE_COPY}.tabs.overview` },
  { id: "profile", labelKey: `${PROFILE_COPY}.tabs.profile` },
  { id: "crm", labelKey: `${PROFILE_COPY}.tabs.crm` },
  { id: "advanced", labelKey: `${PROFILE_COPY}.tabs.advanced` },
];

const VALID_PAGE_TABS = new Set(PAGE_TABS.map((tab) => tab.id));

const ACCOUNT_TYPES = ["prospect", "partner", "customer", "vendor", "other"];

function partnerStatusLabel(status, t) {
  const key = String(status || "none");
  return t(`${COPY}.partnerStatus.${key}`, {}, key);
}

function commercialStatusLabel(status, t) {
  const key = String(status || "unknown");
  return t(`${PROFILE_COPY}.commercial.status.${key}`, {}, formatStatusLabel(status));
}

function CollapsiblePanel({ title, description, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="cm-agency-profile__collapsible">
      <button
        type="button"
        className="cm-agency-profile__collapsible-trigger"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <div className="cm-agency-profile__collapsible-copy">
          <strong>{title}</strong>
          {description ? <span className="muted">{description}</span> : null}
        </div>
        <span className="cm-agency-profile__collapsible-icon" aria-hidden>
          {open ? "−" : "+"}
        </span>
      </button>
      {open ? <div className="cm-agency-profile__collapsible-body">{children}</div> : null}
    </section>
  );
}

function OverviewLeadsPanel({ leads, leadsPath, viewBase, t }) {
  return (
    <section className="cm-agency-profile__panel">
      <div className="cm-agency-profile__panel-head">
        <h2>{t(`${PROFILE_COPY}.leads.title`)}</h2>
        {leadsPath ? (
          <Link className="cm-agency-profile__inline-link" to={leadsPath}>
            {t(`${PROFILE_COPY}.leads.viewAll`)}
          </Link>
        ) : null}
      </div>
      {leads.length ? (
        <div className="cm-agency-profile__leads">
          {leads.map((lead) => (
            <article key={lead.opportunityId} className="cm-agency-profile__lead-card">
              <div className="cm-agency-profile__lead-head">
                <strong>{lead.title}</strong>
                <span className="cm-agency-profile__lead-status">
                  {formatStatusLabel(lead.status)}
                </span>
              </div>
              <p className="muted">
                {lead.contactName || lead.contactEmail || "—"}
                {lead.submittedAt ? ` · ${formatClientDateTime(lead.submittedAt)}` : ""}
              </p>
              {viewBase ? (
                <Link to={`${viewBase}/opportunities/${lead.opportunityId}`}>
                  {t(`${PROFILE_COPY}.leads.open`)}
                </Link>
              ) : null}
            </article>
          ))}
        </div>
      ) : (
        <p className="muted">{t(`${PROFILE_COPY}.leads.empty`)}</p>
      )}
    </section>
  );
}

function OverviewActivityPanel({ activities, t }) {
  const recent = activities.slice(0, 6);

  return (
    <section className="cm-agency-profile__panel">
      <h2>{t(`${PROFILE_COPY}.activity.title`)}</h2>
      {recent.length ? (
        <ol className="property-detail-timeline cm-agency-profile__timeline">
          {recent.map((activity) => (
            <li key={activity.id}>
              <div className="property-detail-timeline__marker" aria-hidden="true" />
              <div className="property-detail-timeline__body">
                <p className="property-detail-timeline__title">
                  {activity.subject || activity.activityType}
                </p>
                <p className="property-detail-timeline__meta">
                  {formatClientDateTime(activity.createdAt)}
                  {activity.description ? ` · ${activity.description}` : ""}
                </p>
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p className="muted">{t(`${PROFILE_COPY}.activity.empty`)}</p>
      )}
    </section>
  );
}

function AgencyMediaPanel({
  agencySlug,
  clientViewId,
  media,
  t,
  onSave,
  saving,
  message,
  error,
}) {
  const [logoUrl, setLogoUrl] = useState(media?.logoUrl || "");
  const [logoImageMedia, setLogoImageMedia] = useState(media?.logoImageMedia || null);
  const [galleryUrls, setGalleryUrls] = useState(media?.galleryUrls || []);
  const [galleryItems, setGalleryItems] = useState(media?.galleryItems || []);

  useEffect(() => {
    setLogoUrl(media?.logoUrl || "");
    setLogoImageMedia(media?.logoImageMedia || null);
    setGalleryUrls(media?.galleryUrls || []);
    setGalleryItems(media?.galleryItems || (media?.galleryUrls || []).map((url) => ({ url })));
  }, [media]);

  const handleAddGalleryImage = (url, fileMeta = null) => {
    if (!url || galleryUrls.includes(url)) return;
    setGalleryUrls((current) => [...current, url].slice(0, 12));
    setGalleryItems((current) =>
      [
        ...current,
        {
          url,
          imageMediaAssetId: fileMeta?.imageMedia?.assetId || null,
          variants: fileMeta?.imageMedia?.variants || null,
        },
      ].slice(0, 12)
    );
  };

  const handleRemoveGalleryImage = (url) => {
    setGalleryUrls((current) => current.filter((item) => item !== url));
    setGalleryItems((current) => current.filter((item) => item.url !== url));
  };

  return (
    <section className="cm-agency-profile__panel">
      <h2>{t(`${PROFILE_COPY}.media.title`)}</h2>
      <p className="muted">{t(`${PROFILE_COPY}.media.subtitle`)}</p>

      <div className="cm-agency-profile__media-grid">
        <div className="cm-agency-profile__media-block">
          <ClientPortalImageUpload
            clientViewId={clientViewId}
            kind="agency_logo"
            entityId={agencySlug}
            label={t(`${PROFILE_COPY}.media.logo`)}
            hint={t(`${PROFILE_COPY}.media.logoHint`)}
            value={logoUrl}
            onChange={(url) => {
              setLogoUrl(url);
              if (!url) setLogoImageMedia(null);
            }}
            onUploaded={(file) => {
              setLogoImageMedia(
                file?.imageMedia
                  ? {
                      assetId: file.imageMedia.assetId || null,
                      variants: file.imageMedia.variants || null,
                      publicUrl: file.imageMedia.publicUrl || file.url,
                    }
                  : null
              );
            }}
            disabled={saving}
          />
        </div>

        <div className="cm-agency-profile__media-block cm-agency-profile__media-block--gallery">
          <div className="cm-agency-profile__gallery-head">
            <div>
              <strong>{t(`${PROFILE_COPY}.media.gallery`)}</strong>
              <p className="muted">{t(`${PROFILE_COPY}.media.galleryHint`)}</p>
            </div>
            <span className="cm-agency-profile__gallery-count">
              {t(`${PROFILE_COPY}.media.galleryCount`, { count: galleryUrls.length })}
            </span>
          </div>

          {galleryUrls.length ? (
            <div className="cm-agency-profile__gallery-grid">
              {galleryUrls.map((url) => (
                <figure key={url} className="cm-agency-profile__gallery-item">
                  <img src={resolveClientPortalAssetUrl(url)} alt="" />
                  <button
                    type="button"
                    className="cm-agency-profile__gallery-remove"
                    disabled={saving}
                    onClick={() => handleRemoveGalleryImage(url)}
                  >
                    {t(`${PROFILE_COPY}.media.removeImage`)}
                  </button>
                </figure>
              ))}
            </div>
          ) : (
            <p className="muted">{t(`${PROFILE_COPY}.media.galleryEmpty`)}</p>
          )}

          {galleryUrls.length < 12 ? (
            <ClientPortalImageUpload
              clientViewId={clientViewId}
              kind="agency_gallery"
              entityId={agencySlug}
              label={t(`${PROFILE_COPY}.media.addImage`)}
              value=""
              onChange={(url) => handleAddGalleryImage(url)}
              onUploaded={(file) => handleAddGalleryImage(file?.url, file)}
              disabled={saving}
            />
          ) : null}
        </div>
      </div>

      {error ? <p className="cm-agency-profile__error">{error}</p> : null}
      {message ? <p className="cm-agency-profile__success">{message}</p> : null}
      <button
        type="button"
        className="partner-dashboard-btn partner-dashboard-btn--primary"
        disabled={saving}
        onClick={() => onSave({ logoUrl, logoImageMedia, galleryUrls, galleryItems })}
      >
        {saving ? t(`${PROFILE_COPY}.media.saving`) : t(`${PROFILE_COPY}.media.save`)}
      </button>
    </section>
  );
}

function AgencyCommercialPanel({ commercial, t, formatMoney, embedded = false }) {
  const campaigns = commercial?.campaigns || [];
  const placements = commercial?.placements || [];
  const summary = commercial?.summary || {};

  const content = (
    <>
      <div className="cm-agency-profile__commercial-kpis">
        <article className="cm-agency-profile__commercial-kpi">
          <span>{t(`${PROFILE_COPY}.commercial.metrics.activeCampaigns`)}</span>
          <strong>{summary.activeCampaigns ?? 0}</strong>
        </article>
        <article className="cm-agency-profile__commercial-kpi">
          <span>{t(`${PROFILE_COPY}.commercial.metrics.activeBoosts`)}</span>
          <strong>{summary.activePlacements ?? 0}</strong>
        </article>
        <article className="cm-agency-profile__commercial-kpi">
          <span>{t(`${PROFILE_COPY}.commercial.metrics.scheduledBoosts`)}</span>
          <strong>{summary.scheduledPlacements ?? 0}</strong>
        </article>
      </div>

      <div className="cm-agency-profile__commercial-section">
        <h3>{t(`${PROFILE_COPY}.commercial.plansTitle`)}</h3>
        {campaigns.length ? (
          <div className="cm-agency-profile__commercial-list">
            {campaigns.map((campaign) => (
              <article key={campaign.id} className="cm-agency-profile__commercial-card">
                <div className="cm-agency-profile__commercial-card-head">
                  <strong>{campaign.name}</strong>
                  <span className={`cm-agency-profile__commercial-badge cm-agency-profile__commercial-badge--${campaign.status}`}>
                    {commercialStatusLabel(campaign.status, t)}
                  </span>
                </div>
                <dl className="cm-agency-profile__commercial-meta">
                  <div>
                    <dt>{t(`${PROFILE_COPY}.commercial.fields.plan`)}</dt>
                    <dd>{campaign.plan?.name || t(`${PROFILE_COPY}.commercial.noPlan`)}</dd>
                  </div>
                  <div>
                    <dt>{t(`${PROFILE_COPY}.commercial.fields.billing`)}</dt>
                    <dd>
                      {campaign.contractedAmount != null
                        ? formatMoney(campaign.contractedAmount, { currency: campaign.currency || "COP" })
                        : "—"}
                      {campaign.billingPeriod ? ` · ${campaign.billingPeriod}` : ""}
                    </dd>
                  </div>
                  <div>
                    <dt>{t(`${PROFILE_COPY}.commercial.fields.payment`)}</dt>
                    <dd>{commercialStatusLabel(campaign.paymentStatus, t)}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        ) : (
          <p className="muted">{t(`${PROFILE_COPY}.commercial.plansEmpty`)}</p>
        )}
      </div>

      <div className="cm-agency-profile__commercial-section">
        <h3>{t(`${PROFILE_COPY}.commercial.boostsTitle`)}</h3>
        {placements.length ? (
          <div className="cm-agency-profile__commercial-list">
            {placements.map((placement) => (
              <article key={placement.id} className="cm-agency-profile__commercial-card">
                <div className="cm-agency-profile__commercial-card-head">
                  <strong>{placement.name || placement.slotName}</strong>
                  <span className={`cm-agency-profile__commercial-badge cm-agency-profile__commercial-badge--${placement.status}`}>
                    {commercialStatusLabel(placement.status, t)}
                  </span>
                </div>
                <dl className="cm-agency-profile__commercial-meta">
                  <div>
                    <dt>{t(`${PROFILE_COPY}.commercial.fields.slot`)}</dt>
                    <dd>{placement.slotName || placement.slotKey || "—"}</dd>
                  </div>
                  <div>
                    <dt>{t(`${PROFILE_COPY}.commercial.fields.surface`)}</dt>
                    <dd>{placement.pageContext || placement.slotType || "—"}</dd>
                  </div>
                  <div>
                    <dt>{t(`${PROFILE_COPY}.commercial.fields.campaign`)}</dt>
                    <dd>{placement.campaignName || "—"}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        ) : (
          <p className="muted">{t(`${PROFILE_COPY}.commercial.boostsEmpty`)}</p>
        )}
      </div>
    </>
  );

  if (embedded) {
    return <div className="cm-agency-profile__commercial-embedded">{content}</div>;
  }

  return (
    <section className="cm-agency-profile__panel">
      <h2>{t(`${PROFILE_COPY}.commercial.title`)}</h2>
      <p className="muted">{t(`${PROFILE_COPY}.commercial.subtitle`)}</p>
      {content}
    </section>
  );
}

function formatPriceRange(agency, formatMoney) {
  if (agency?.priceMinCop == null && agency?.priceMaxCop == null) return "—";
  const min = agency.priceMinCop != null ? formatMoney(agency.priceMinCop) : "—";
  const max = agency.priceMaxCop != null ? formatMoney(agency.priceMaxCop) : "—";
  if (min === max) return min;
  return `${min} – ${max}`;
}

function AttributeGrid({ rows = [] }) {
  if (!rows.length) return null;
  return (
    <dl className="cm-agency-profile__attributes">
      {rows.map((row) => (
        <div key={row.label} className="cm-agency-profile__attribute">
          <dt>{row.label}</dt>
          <dd>{row.value ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

function CrmAccountForm({ account, t, onSave, saving, message, error }) {
  const [form, setForm] = useState({
    accountName: "",
    accountType: "prospect",
    email: "",
    phone: "",
    website: "",
    notes: "",
  });

  useEffect(() => {
    if (!account) return;
    setForm({
      accountName: account.accountName || "",
      accountType: account.accountType || "prospect",
      email: account.email || "",
      phone: account.phone || "",
      website: account.website || "",
      notes: account.notes || "",
    });
  }, [account]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave(form);
  };

  return (
    <form className="cm-agency-profile__crm-form" onSubmit={handleSubmit}>
      <div className="cm-agency-profile__crm-grid">
        <label>
          <span>{t(`${PROFILE_COPY}.crm.fields.accountName`)}</span>
          <input
            value={form.accountName}
            onChange={(event) => setForm((prev) => ({ ...prev, accountName: event.target.value }))}
          />
        </label>
        <label>
          <span>{t(`${PROFILE_COPY}.crm.fields.accountType`)}</span>
          <select
            value={form.accountType}
            onChange={(event) => setForm((prev) => ({ ...prev, accountType: event.target.value }))}
          >
            {ACCOUNT_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`${PROFILE_COPY}.crm.accountTypes.${type}`, {}, type)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>{t(`${PROFILE_COPY}.crm.fields.email`)}</span>
          <input
            type="email"
            value={form.email}
            onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
          />
        </label>
        <label>
          <span>{t(`${PROFILE_COPY}.crm.fields.phone`)}</span>
          <input
            value={form.phone}
            onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
          />
        </label>
        <label className="cm-agency-profile__crm-span">
          <span>{t(`${PROFILE_COPY}.crm.fields.website`)}</span>
          <input
            value={form.website}
            onChange={(event) => setForm((prev) => ({ ...prev, website: event.target.value }))}
          />
        </label>
        <label className="cm-agency-profile__crm-span">
          <span>{t(`${PROFILE_COPY}.crm.fields.notes`)}</span>
          <textarea
            rows={5}
            value={form.notes}
            onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
          />
        </label>
      </div>
      {error ? <p className="cm-agency-profile__error">{error}</p> : null}
      {message ? <p className="cm-agency-profile__success">{message}</p> : null}
      <button type="submit" className="partner-dashboard-btn partner-dashboard-btn--primary" disabled={saving}>
        {saving ? t(`${PROFILE_COPY}.crm.saving`) : t(`${PROFILE_COPY}.crm.save`)}
      </button>
    </form>
  );
}

export default function CasaMapaAdminAgencyDetailPage() {
  const { agencySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { path, clientViewId, viewBase } = useCasaMapaAdminPortal();
  const { t } = useLocale();
  const { formatMoney } = useCurrency();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openingPortal, setOpeningPortal] = useState(false);
  const [crmSaving, setCrmSaving] = useState(false);
  const [crmMessage, setCrmMessage] = useState("");
  const [crmError, setCrmError] = useState("");
  const [mediaSaving, setMediaSaving] = useState(false);
  const [mediaMessage, setMediaMessage] = useState("");
  const [mediaError, setMediaError] = useState("");

  const sectionParam = searchParams.get("section");
  const activeTab = VALID_PAGE_TABS.has(sectionParam) ? sectionParam : "overview";

  const setActiveTab = useCallback(
    (tabId) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (tabId === "overview") {
            next.delete("section");
          } else {
            next.set("section", tabId);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const loadProfile = useCallback(async () => {
    if (!clientViewId || !agencySlug) return;
    try {
      setLoading(true);
      setError("");
      const result = await fetchCasaMapaAdminAgency(clientViewId, agencySlug);
      setProfile(result || null);
    } catch (err) {
      setError(err.message || t(`${PROFILE_COPY}.loadError`));
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [agencySlug, clientViewId, t]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const agency = profile?.agency || null;
  const crm = profile?.crm || null;
  const registry = agency?.registry || null;

  const registryRows = useMemo(() => {
    if (!registry) return [];
    return [
      { label: t(`${PROFILE_COPY}.registry.publicId`), value: registry.publicId },
      { label: t(`${PROFILE_COPY}.registry.slug`), value: registry.slug },
      { label: t(`${PROFILE_COPY}.registry.legalName`), value: registry.legalName },
      { label: t(`${PROFILE_COPY}.registry.publicationStatus`), value: registry.publicationStatus },
      { label: t(`${PROFILE_COPY}.registry.partnerStatus`), value: partnerStatusLabel(registry.partnerStatus, t) },
      { label: t(`${PROFILE_COPY}.registry.profileCompleteness`), value: registry.profileCompleteness },
      { label: t(`${PROFILE_COPY}.registry.website`), value: registry.websiteUrl },
      {
        label: t(`${PROFILE_COPY}.registry.sourceKeys`),
        value: (registry.sourceKeys || []).join(", ") || "—",
      },
      {
        label: t(`${PROFILE_COPY}.registry.serviceAreas`),
        value: (registry.configuredServiceAreas || []).map((area) => area.name).join(", ") || "—",
      },
    ];
  }, [registry, t]);

  const handleViewAsAgent = async () => {
    if (!clientViewId || openingPortal) return;
    try {
      setOpeningPortal(true);
      const result = await startCasaMapaAdminAgencyPortalSession(clientViewId, agencySlug);
      window.location.assign(result.redirectUrl || `${CASAMAPA_PARTNER_PORTAL_BASE}/dashboard`);
    } catch (err) {
      setError(err.message || t(`${COPY}.viewAsAgentError`));
      setOpeningPortal(false);
    }
  };

  const handleSaveCrm = async (payload) => {
    if (!clientViewId || !agencySlug) return;
    try {
      setCrmSaving(true);
      setCrmError("");
      setCrmMessage("");
      await patchCasaMapaAdminAgencyCrm(clientViewId, agencySlug, payload);
      setCrmMessage(t(`${PROFILE_COPY}.crm.saved`));
      await loadProfile();
    } catch (err) {
      setCrmError(err.message || t(`${PROFILE_COPY}.crm.saveError`));
    } finally {
      setCrmSaving(false);
    }
  };

  const handleSaveMedia = async (payload) => {
    if (!clientViewId || !agencySlug) return;
    try {
      setMediaSaving(true);
      setMediaError("");
      setMediaMessage("");
      await patchCasaMapaAdminAgencyProfile(clientViewId, agencySlug, payload);
      setMediaMessage(t(`${PROFILE_COPY}.media.saved`));
      await loadProfile();
    } catch (err) {
      setMediaError(err.message || t(`${PROFILE_COPY}.media.saveError`));
    } finally {
      setMediaSaving(false);
    }
  };

  const leadsPath = viewBase ? `${viewBase}/opportunities` : null;
  const heroLogoUrl = agency?.logoUrl ? resolveClientPortalAssetUrl(agency.logoUrl) : null;
  const opportunities = crm?.opportunities || [];
  const activities = crm?.activities || [];
  const hasCommercial =
    (profile?.commercial?.campaigns?.length ?? 0) > 0 ||
    (profile?.commercial?.placements?.length ?? 0) > 0;

  if (!clientViewId) {
    return <p className="muted">Select a CasaMapa client view to continue.</p>;
  }

  return (
    <section className="partner-agencies-page cm-agency-profile">
      <header className="partner-agencies-page__hero partner-dashboard-hero cm-agency-profile__hero">
        <div className="cm-agency-profile__hero-main">
          <p className="partner-dashboard-hero__eyebrow">{t("realEstate.casaMapa.admin.portalEyebrow")}</p>
          <div className="cm-agency-profile__identity">
            {heroLogoUrl ? (
              <img src={heroLogoUrl} alt="" className="cm-agency-profile__logo" />
            ) : (
              <span className="cm-agency-profile__mark" aria-hidden="true">
                {String(agency?.name || "?").slice(0, 1)}
              </span>
            )}
            <div>
              <h1>{agency?.name || t(`${PROFILE_COPY}.title`)}</h1>
              <p className="muted">{agency?.slug || agencySlug}</p>
              {agency ? (
                <span className={`partner-agency-card__status partner-agency-card__status--${agency.partnerStatus === "partner" || agency.partnerStatus === "active" ? "partner" : "listed"}`}>
                  {partnerStatusLabel(agency.partnerStatus, t)}
                </span>
              ) : null}
            </div>
          </div>
          {agency?.description ? <p className="cm-agency-profile__description">{agency.description}</p> : null}
        </div>
        <div className="cm-agency-profile__hero-actions">
          {agency ? (
            <>
              <button
                type="button"
                className="partner-dashboard-btn partner-dashboard-btn--primary"
                onClick={handleViewAsAgent}
                disabled={openingPortal}
              >
                {openingPortal ? t(`${COPY}.viewAsAgentOpening`) : t(`${COPY}.viewAsAgent`)}
              </button>
              <nav className="cm-agency-profile__hero-links" aria-label={t(`${PROFILE_COPY}.overview.quickActions`)}>
                <Link className="cm-agency-profile__hero-link" to={path(`agencies/${agency.slug}/inventory`)}>
                  {t(`${COPY}.reviewInventory`)}
                </Link>
                {agency.profileUrl ? (
                  <a className="cm-agency-profile__hero-link" href={agency.profileUrl} target="_blank" rel="noreferrer">
                    {t("realEstate.casaMapa.admin.viewPublic")}
                  </a>
                ) : null}
                <Link className="cm-agency-profile__hero-link" to={path("agencies")}>
                  {t(`${PROFILE_COPY}.backToList`)}
                </Link>
              </nav>
            </>
          ) : (
            <Link className="partner-dashboard-btn" to={path("agencies")}>
              {t(`${PROFILE_COPY}.backToList`)}
            </Link>
          )}
        </div>
      </header>

      {error ? <div className="partner-agencies-page__error" role="alert"><p>{error}</p></div> : null}
      {loading ? <p className="muted">{t(`${PROFILE_COPY}.loading`)}</p> : null}

      {!loading && agency ? (
        <>
          <nav className="cm-agency-profile__tabs" role="tablist" aria-label={t(`${PROFILE_COPY}.title`)}>
            {PAGE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`cm-agency-profile__tab${activeTab === tab.id ? " cm-agency-profile__tab--active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {t(tab.labelKey, {}, tab.id)}
              </button>
            ))}
          </nav>

          {activeTab === "overview" ? (
            <div className="cm-agency-profile__tab-pane">
              <div className="partner-kpi-grid cm-agency-profile__kpi-grid cm-agency-profile__kpi-grid--compact">
                <article className="partner-kpi-card partner-kpi-card--blue">
                  <span className="partner-kpi-card__label">{t(`${COPY}.activeListings`)}</span>
                  <strong className="partner-kpi-card__value">{(agency.activeListingCount ?? 0).toLocaleString()}</strong>
                </article>
                <article className="partner-kpi-card partner-kpi-card--green">
                  <span className="partner-kpi-card__label">{t(`${PROFILE_COPY}.crm.metrics.newLeads`)}</span>
                  <strong className="partner-kpi-card__value">{(crm?.leadMetrics?.new ?? 0).toLocaleString()}</strong>
                </article>
                <article className="partner-kpi-card partner-kpi-card--amber">
                  <span className="partner-kpi-card__label">{t("realEstate.casaMapa.admin.listingType.sale")}</span>
                  <strong className="partner-kpi-card__value">{(agency.saleListingCount ?? 0).toLocaleString()}</strong>
                </article>
                <article className="partner-kpi-card partner-kpi-card--slate">
                  <span className="partner-kpi-card__label">{t("realEstate.casaMapa.admin.listingType.rent")}</span>
                  <strong className="partner-kpi-card__value">{(agency.rentListingCount ?? 0).toLocaleString()}</strong>
                </article>
              </div>

              <div className="cm-agency-profile__overview-grid">
                <OverviewLeadsPanel
                  leads={opportunities.slice(0, 5)}
                  leadsPath={leadsPath}
                  viewBase={viewBase}
                  t={t}
                />
                <OverviewActivityPanel activities={activities} t={t} />
              </div>

              <section className="cm-agency-profile__panel cm-agency-profile__panel--muted">
                <h2>{t(`${PROFILE_COPY}.overview.atAGlance`)}</h2>
                <AttributeGrid
                  rows={[
                    {
                      label: t(`${PROFILE_COPY}.crm.metrics.totalLeads`),
                      value: (crm?.leadMetrics?.total ?? 0).toLocaleString(),
                    },
                    {
                      label: t(`${COPY}.priceRange`),
                      value: formatPriceRange(agency, formatMoney),
                    },
                    {
                      label: t(`${PROFILE_COPY}.portal.users`),
                      value: agency.portalAccess?.portalUserCount ?? 0,
                    },
                    {
                      label: t(`${PROFILE_COPY}.registry.serviceAreas`),
                      value: (registry?.configuredServiceAreas || []).map((area) => area.name).join(", ") || "—",
                    },
                  ]}
                />
                <p className="muted cm-agency-profile__hint">
                  {t(`${PROFILE_COPY}.overview.contactHint`)}
                </p>
              </section>
            </div>
          ) : null}

          {activeTab === "profile" ? (
            <div className="cm-agency-profile__tab-pane">
              <AgencyMediaPanel
                agencySlug={agency.slug}
                clientViewId={clientViewId}
                media={profile?.profileMedia}
                t={t}
                onSave={handleSaveMedia}
                saving={mediaSaving}
                message={mediaMessage}
                error={mediaError}
              />
            </div>
          ) : null}

          {activeTab === "crm" ? (
            <div className="cm-agency-profile__tab-pane">
              <section className="cm-agency-profile__panel">
                <div className="cm-agency-profile__panel-head">
                  <h2>{t(`${PROFILE_COPY}.crm.title`)}</h2>
                  {crm?.linked ? (
                    <span className="cm-agency-profile__badge cm-agency-profile__badge--linked">
                      {t(`${PROFILE_COPY}.crm.linked`)}
                    </span>
                  ) : null}
                </div>
                <p className="muted">{t(`${PROFILE_COPY}.crm.subtitle`)}</p>
                <CrmAccountForm
                  account={crm?.account}
                  t={t}
                  onSave={handleSaveCrm}
                  saving={crmSaving}
                  message={crmMessage}
                  error={crmError}
                />
                <AgencyCrmWorkspace
                  clientViewId={clientViewId}
                  agencySlug={agency.slug}
                  contacts={crm?.contacts || []}
                  addresses={crm?.addresses || []}
                  comments={crm?.comments || []}
                  documents={crm?.documents || []}
                  primaryContactId={crm?.account?.primaryContactId || null}
                  t={t}
                  onRefresh={loadProfile}
                />
              </section>
            </div>
          ) : null}

          {activeTab === "advanced" ? (
            <div className="cm-agency-profile__tab-pane">
              <p className="muted cm-agency-profile__advanced-intro">
                {t(`${PROFILE_COPY}.advanced.intro`)}
              </p>

              <CollapsiblePanel
                title={t(`${PROFILE_COPY}.registry.title`)}
                description={t(`${PROFILE_COPY}.advanced.registryHint`)}
              >
                <AttributeGrid rows={registryRows} />
              </CollapsiblePanel>

              <CollapsiblePanel
                title={t(`${PROFILE_COPY}.portal.title`)}
                description={t(`${PROFILE_COPY}.advanced.portalHint`)}
              >
                <AttributeGrid
                  rows={[
                    {
                      label: t(`${PROFILE_COPY}.portal.status`),
                      value: agency.portalAccess?.status || "—",
                    },
                    {
                      label: t(`${PROFILE_COPY}.portal.users`),
                      value: agency.portalAccess?.portalUserCount ?? 0,
                    },
                    {
                      label: t(`${PROFILE_COPY}.portal.previewEmail`),
                      value: agency.portalAccess?.previewEmail || "—",
                    },
                  ]}
                />
              </CollapsiblePanel>

              <CollapsiblePanel
                title={t(`${PROFILE_COPY}.commercial.title`)}
                description={t(`${PROFILE_COPY}.advanced.commercialHint`)}
                defaultOpen={hasCommercial}
              >
                <AgencyCommercialPanel
                  commercial={profile?.commercial}
                  t={t}
                  formatMoney={formatMoney}
                  embedded
                />
              </CollapsiblePanel>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
